import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS, IMAGES } from '@/data/site';
import { LOCATIONS, HUB_PAGES } from '@/data/locations';
import ZipChecker from '@/components/ZipChecker';

const CATEGORY_LINKS = [
  { to: '/shop/exotic', label: 'Exotic Flower' },
  { to: '/shop/prerolls', label: 'Pre-Rolls' },
  { to: '/shop/cartridges', label: 'Vapes & Cartridges' },
  { to: '/shop/edibles', label: 'Edibles' },
  { to: '/shop/concentrates', label: 'Concentrates' },
  { to: '/deals', label: 'Deals & Bundles' },
];

export default function LocationPage({ location }) {
  const others = [...LOCATIONS, ...HUB_PAGES].filter((l) => l.slug !== location.slug).slice(0, 8);
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: location.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://majesticgreensdmv.com/' },
        { '@type': 'ListItem', position: 2, name: 'Washington, DC', item: 'https://majesticgreensdmv.com/cannabis-washington-dc' },
        { '@type': 'ListItem', position: 3, name: location.name, item: `https://majesticgreensdmv.com/${location.slug}` },
      ],
    },
  ];

  return (
    <>
      <SEO title={location.title} description={location.meta} path={`/${location.slug}`} jsonLd={jsonLd} />
      <div data-testid={`location-page-${location.slug}`} className="pb-28 pt-28">
        <div className="grain relative overflow-hidden">
          <img src={IMAGES.dc} alt={`${location.name}, Washington DC`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="relative mx-auto max-w-4xl px-6 py-16">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-bronze">
                <Link to="/cannabis-washington-dc" data-testid="breadcrumb-dc" className="hover:text-bronzehover">Washington, DC</Link>
                <span className="text-ash"> / {location.name}</span>
              </p>
              <h1 className="mt-4 font-serif text-4xl text-ink sm:text-6xl">{location.h1}</h1>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-6">
          {location.intro.map((para, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <p className="mt-6 text-base leading-relaxed text-sage">{para}</p>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-12 border border-gold/30 bg-gold/5 p-8">
              <h2 className="font-serif text-2xl text-ink">Do we serve {location.name}?</h2>
              <p className="mt-2 text-sm text-sage">Confirm your address instantly.</p>
              <div className="mt-6"><ZipChecker /></div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <h2 className="mt-16 font-serif text-3xl text-ink">Available near {location.landmarks}</h2>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {CATEGORY_LINKS.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`location-category-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="group flex items-center justify-between border border-ink/10 px-5 py-4 transition-all hover:border-gold/50 hover:bg-gold/5"
                >
                  <span className="text-sm text-sage group-hover:text-ink">{c.label}</span>
                  <ChevronRight className="h-4 w-4 text-ash transition-all group-hover:translate-x-1 group-hover:text-bronze" />
                </Link>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 border border-ink/10 bg-surface p-8">
              <h2 className="font-serif text-2xl text-ink">How ordering works</h2>
              <ol className="mt-6 space-y-4 text-sm leading-relaxed text-sage">
                <li><strong className="text-ink">1. Browse the live menu</strong> — flower, vapes, edibles, concentrates and more.</li>
                <li><strong className="text-ink">2. Build your cart</strong> — ${BUSINESS.minOrder} minimum order, with quick add throughout.</li>
                <li><strong className="text-ink">3. Check out</strong> — minimal details, two minutes or less.</li>
                <li><strong className="text-ink">4. Verification</strong> — our team calls to confirm your order.</li>
                <li><strong className="text-ink">5. Fulfillment</strong> — prepared and fulfilled by our local DC team.</li>
              </ol>
              <Link to="/how-to-order" data-testid="location-howto-link" className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-bronze hover:text-bronzehover">
                Full ordering guide <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-16 font-serif text-3xl text-ink">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-6">
              {location.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-ink/10">
                  <AccordionTrigger data-testid={`location-faq-${location.slug}-${i}`} className="text-left font-serif text-lg text-ink hover:text-bronze">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-sage">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 flex flex-col items-center border border-gold/30 bg-gold/5 p-10 text-center">
              <h2 className="font-serif text-3xl text-ink">Ready to order in {location.name}?</h2>
              <p className="mt-3 max-w-md text-sm text-sage">${BUSINESS.minOrder} minimum · Adults 21+ · Verification call after checkout</p>
              <Link
                to="/shop"
                data-testid="location-shop-cta"
                className="group mt-8 inline-flex items-center gap-3 bg-gold px-10 py-5 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bronzehover"
              >
                Shop Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="location-phone-cta" className="mt-4 text-sm font-semibold text-bronze underline underline-offset-4">
                Or call {BUSINESS.phone}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <h2 className="mt-16 text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">More areas we serve</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {others.map((l) => (
                <Link
                  key={l.slug}
                  to={`/${l.slug}`}
                  data-testid={`location-crosslink-${l.slug}`}
                  className="border border-ink/10 px-4 py-2 text-xs text-sage transition-colors hover:border-gold/50 hover:text-ink"
                >
                  {l.name}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
